
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Divider } from "primereact/divider";
import { Checkbox } from "primereact/checkbox";

import { useEffect, useState } from "react";
import {
    Controller,
    useFieldArray,
    useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { IAllRequirements } from "../../../models/AllRequirements";
import { Job_Type, Req_Term, IndustryType, } from "../../../models/RequirementMast";
import { JVInput } from "../../../shared/JVInput";
import { JVCheckbox } from "../../../shared/JVCheckbox";
import { JVDropdown } from "../../../shared/JVDropdown";

interface IProps {
    open: boolean;
    hideDialog: () => void;
    requirement: IAllRequirements;
    toast: any;
}

interface IJobSection {
    title: string;
    description: string;
    icon?: string;
}

interface IPUblisReq {
    req_ID: number;
    req_Posted_Date: Date | null;
    req_Job_Title: string;
    req_Duration: string;
    req_Term: string;
    job_Type: string;
    location: string;
    industryType?: number;
    industryTypeName?: string;
    active: boolean;

    no_Of_Openings: number;
    application_Deadline: string;

    description: string;
    aboutRoleTitle: string;
    aboutRoleMission: string;

    keyResponsibilities: IJobSection[];
    requiredSkillsExperience: IJobSection[];
    preferredQualifications: IJobSection[];
    benefitsPerksPackage: IJobSection[];

    primaryLocation: string;
    workModel: string;
    workModelDescription: string;

    salaryMin: number | "";
    salaryMax: number | "";
    salaryCurrency: string;
    salaryPeriod: string;
    equityDescription: string;

    companyName: string;
    companyCategory: string;
    isVerifiedEmployer: boolean;

    referralReward: number | "";
    referralRewardCurrency: string;
    referralDescription: string;
    referralMilestoneDays: number | "";

    hiringPartnerName: string;
    hiringPartnerDesignation: string;
    hiringPartnerImage: string;
    hiringPartnerMessage: string;
}

const emptySection: IJobSection = {
    title: "",
    description: "",
};

const EmptyRequirement: IPUblisReq = {
    req_ID: 0,
    req_Posted_Date: null,
    req_Job_Title: "",
    req_Duration: "",
    req_Term: "",
    job_Type: "",
    location: "",
    industryType: IndustryType.IT,
    active: false,

    no_Of_Openings: 1,
    application_Deadline: "",

    description: "",
    aboutRoleTitle: "",
    aboutRoleMission: "",

    keyResponsibilities: [{ ...emptySection }],
    requiredSkillsExperience: [{ ...emptySection }],
    preferredQualifications: [{ ...emptySection }],
    benefitsPerksPackage: [
        { ...emptySection, icon: "HeartPulse" },
    ],

    primaryLocation: "",
    workModel: "",
    workModelDescription: "",

    salaryMin: "",
    salaryMax: "",
    salaryCurrency: "INR",
    salaryPeriod: "Year",
    equityDescription: "",

    companyName: "",
    companyCategory: "",
    isVerifiedEmployer: false,

    referralReward: "",
    referralRewardCurrency: "INR",
    referralDescription: "",
    referralMilestoneDays: "",

    hiringPartnerName: "",
    hiringPartnerDesignation: "",
    hiringPartnerImage: "",
    hiringPartnerMessage: "",
};

const schema = yup.object({
    req_ID: yup.number().required("Req ID is required"),
    req_Posted_Date: yup.date().nullable().required("Posted date is required"),
    req_Job_Title: yup.string().trim().required("Job Title is required"),
    req_Duration: yup.string().required("Duration is required"),
    req_Term: yup.string().required("Job Term is required"),
    job_Type: yup.string().required("Job Type is required"),
    location: yup.string().required("Location is required"),
    industryType: yup.number().required("Please select industry type"),
    active: yup.boolean().required(),

    no_Of_Openings: yup
        .number()
        .typeError("Enter number of openings")
        .integer("Enter a whole number")
        .min(1, "At least one opening is required")
        .required("Number of openings is required"),

    application_Deadline: yup.string().default(""),
    description: yup.string().default(""),
    aboutRoleTitle: yup.string().default(""),
    aboutRoleMission: yup.string().default(""),

    keyResponsibilities: yup.array().of(
        yup.object({
            title: yup.string().default(""),
            description: yup.string().default(""),
        })
    ).required(),

    requiredSkillsExperience: yup.array().of(
        yup.object({
            title: yup.string().default(""),
            description: yup.string().default(""),
        })
    ).required(),

    preferredQualifications: yup.array().of(
        yup.object({
            title: yup.string().default(""),
            description: yup.string().default(""),
        })
    ).required(),

    benefitsPerksPackage: yup.array().of(
        yup.object({
            title: yup.string().default(""),
            description: yup.string().default(""),
            icon: yup.string().optional(),
        })
    ).required(),

    primaryLocation: yup.string().default(""),
    workModel: yup.string().default(""),
    workModelDescription: yup.string().default(""),

    salaryMin: yup.mixed<number | "">().defined(),
    salaryMax: yup.mixed<number | "">().defined(),
    salaryCurrency: yup.string().default("INR"),
    salaryPeriod: yup.string().default("Year"),
    equityDescription: yup.string().default(""),

    companyName: yup.string().default(""),
    companyCategory: yup.string().default(""),
    isVerifiedEmployer: yup.boolean().required(),

    referralReward: yup.mixed<number | "">().defined(),
    referralRewardCurrency: yup.string().default("INR"),
    referralDescription: yup.string().default(""),
    referralMilestoneDays: yup.mixed<number | "">().defined(),

    hiringPartnerName: yup.string().default(""),
    hiringPartnerDesignation: yup.string().default(""),
    hiringPartnerImage: yup.string().default(""),
    hiringPartnerMessage: yup.string().default(""),
});

const industryTypeOptions = [
    { id: IndustryType.IT, name: "IT" },
    { id: IndustryType.NonIT, name: "Non-IT" },
];

const termOptions = [
    { id: Req_Term.Contract, name: "Contract" },
    { id: Req_Term.ContractToHire, name: "Contract To Hire" },
    { id: Req_Term.Permanent, name: "Permanent" },
];

const jobTypeOptions = [
    { id: Job_Type.Onsite, name: "On-site" },
    { id: Job_Type.Hybrid, name: "Hybrid" },
    { id: Job_Type.Remote, name: "Remote" },
];

const salaryPeriodOptions = [
    { id: "Year", name: "Year" },
    { id: "Month", name: "Month" },
];

const benefitIconOptions = [
    { id: "HeartPulse", name: "Medical" },
    { id: "Monitor", name: "Monitor" },
    { id: "GraduationCap", name: "Education" },
    { id: "Plane", name: "Travel" },
];

export const PublishReqPublic = (props: IProps) => {
    const [loading, setLoading] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<IPUblisReq>({
        resolver: yupResolver(schema) as any,
        defaultValues: EmptyRequirement,
        shouldFocusError: false,
    });

    const {
        fields: responsibilityFields,
        append: appendResponsibility,
        remove: removeResponsibility,
    } = useFieldArray({
        control,
        name: "keyResponsibilities",
    });

    const {
        fields: skillFields,
        append: appendSkill,
        remove: removeSkill,
    } = useFieldArray({
        control,
        name: "requiredSkillsExperience",
    });

    const {
        fields: qualificationFields,
        append: appendQualification,
        remove: removeQualification,
    } = useFieldArray({
        control,
        name: "preferredQualifications",
    });

    const {
        fields: benefitFields,
        append: appendBenefit,
        remove: removeBenefit,
    } = useFieldArray({
        control,
        name: "benefitsPerksPackage",
    });

    useEffect(() => {
        if (!props.open) return;

        const req = props.requirement;

        reset({
            ...EmptyRequirement,

            req_ID: req.req_ID ?? 0,
            req_Posted_Date: req.pDate
                ? new Date(req.pDate)
                : new Date(),

            req_Job_Title: req.req_Job_Title ?? "",
            req_Duration: req.req_Duration ?? "",
            req_Term: String(req.req_Term ?? Req_Term.Permanent),
            job_Type: String(req.job_Type ?? Job_Type.Hybrid),
            location: req.location ?? "",
            active: req.hold ?? false,
        });
    }, [props.open, props.requirement, reset]);

    const onHide = () => {
        setLoading(false);
        reset(EmptyRequirement);
        props.hideDialog();
    };

    const onSubmit = async (body: IPUblisReq) => {
        setLoading(true);

        try {
            const newBody = {
                req_ID: body.req_ID,
                industryType: Number(body.industryType),
                req_Posted_Date: body.req_Posted_Date
                    ? new Date(body.req_Posted_Date).toISOString()
                    : null,

                req_Job_Title: body.req_Job_Title.trim(),
                req_Duration: body.req_Duration.trim(),

                req_Term: Number(body.req_Term),
                job_Type: Number(body.job_Type),
                location: body.location.trim(),

                // Keep the original requirement status behavior.
                active: body.active === true ? false : true,

                no_Of_Openings: Number(body.no_Of_Openings),

                application_Deadline: body.application_Deadline
                    ? new Date(body.application_Deadline).toISOString()
                    : null,

                description: body.description.trim() || null,
                aboutRoleTitle: body.aboutRoleTitle.trim() || null,
                aboutRoleMission: body.aboutRoleMission.trim() || null,

                keyResponsibilities: body.keyResponsibilities,
                requiredSkillsExperience: body.requiredSkillsExperience,
                preferredQualifications: body.preferredQualifications,
                benefitsPerksPackage: body.benefitsPerksPackage,

                primaryLocation: body.primaryLocation.trim() || null,
                workModel: body.workModel.trim() || null,
                workModelDescription:
                    body.workModelDescription.trim() || null,

                salaryMin:
                    body.salaryMin === "" ? null : Number(body.salaryMin),

                salaryMax:
                    body.salaryMax === "" ? null : Number(body.salaryMax),

                salaryCurrency: body.salaryCurrency || null,
                salaryPeriod: body.salaryPeriod || null,
                equityDescription: body.equityDescription.trim() || null,

                companyName: body.companyName.trim() || null,
                companyCategory: body.companyCategory.trim() || null,
                isVerifiedEmployer: body.isVerifiedEmployer,

                referralReward:
                    body.referralReward === ""
                        ? null
                        : Number(body.referralReward),

                referralRewardCurrency:
                    body.referralRewardCurrency || null,

                referralDescription:
                    body.referralDescription.trim() || null,

                referralMilestoneDays:
                    body.referralMilestoneDays === ""
                        ? null
                        : Number(body.referralMilestoneDays),

                hiringPartnerName:
                    body.hiringPartnerName.trim() || null,

                hiringPartnerDesignation:
                    body.hiringPartnerDesignation.trim() || null,

                hiringPartnerImage:
                    body.hiringPartnerImage.trim() || null,

                hiringPartnerMessage:
                    body.hiringPartnerMessage.trim() || null,
            };

            const params = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    // Add Authorization through your existing secure
                    // authentication configuration.
                },
                body: JSON.stringify(newBody),
            };

            const response = await fetch(
                "https://localhost:7299/api/Jobs",
                params
            );

            if (!response.ok) {
                const message = await response.text();

                throw new Error(
                    message || "Failed to publish requirement"
                );
            }

            props.toast.current.show({
                severity: "success",
                detail: "Requirement Published Successfully",
                life: 3000,
            });

            onHide();
        } catch (error) {
            console.error("Publish requirement error:", error);

            props.toast.current.show({
                severity: "error",
                detail:
                    error instanceof Error
                        ? error.message
                        : "Failed to publish requirement",
                life: 4000,
            });
        } finally {
            setLoading(false);
        }
    };

    const dFooter = () => (
        <div className="col-12 flex gap-3 justify-content-end">
            <Button
                loading={loading}
                disabled={loading}
                form="publishRequirementForm"
                label="SAVE"
                type="submit"
                style={{ width: "120px" }}
            />

            <Button
                className="p-button-outlined surface-200"
                style={{ width: "100px" }}
                label="CANCEL"
                onClick={onHide}
                type="button"
                disabled={loading}
            />
        </div>
    );



    const renderDynamicSection = (
        title: string,
        fields: any[],
        fieldName:
            | "keyResponsibilities"
            | "requiredSkillsExperience"
            | "preferredQualifications"
            | "benefitsPerksPackage",
        append: (value: any) => void,
        remove: (index: number) => void,
        showIcon = false
    ) => (
        <div className="col-12">
            <div className="flex justify-content-between align-items-center gap-3 mb-3">
                <h3 className="text-lg font-semibold m-0">{title}</h3>

                <Button
                    type="button"
                    label="Add More"
                    icon="pi pi-plus"
                    // outlined
                    onClick={() =>
                        append({
                            ...emptySection,
                            ...(showIcon ? { icon: "HeartPulse" } : {}),
                        })
                    }
                />
            </div>

            {fields.map((item, index) => (
                <div
                    key={item.id}
                    className="grid border-1 surface-border border-round p-3 mb-3"
                >
                    <div className="col-12 flex justify-content-between align-items-center">
                        <strong>
                            {title} {index + 1}
                        </strong>

                        <Button
                            type="button"
                            label="Remove"
                            icon="pi pi-trash"
                            disabled={fields.length === 1}
                            onClick={() => remove(index)}
                        />
                    </div>

                    <JVInput
                        column={showIcon ? 4 : 6}
                        label="Title"
                        control={control}
                        name={`${fieldName}.${index}.title` as any}
                    />

                    <div className={showIcon ? "col-12 md:col-5" : "col-12 md:col-6"}>
                        <label className="block mb-2">Description</label>

                        <Controller
                            name={`${fieldName}.${index}.description` as any}
                            control={control}
                            render={({ field }) => (
                                <InputTextarea
                                    {...field}
                                    value={field.value ?? ""}
                                    rows={2}
                                    autoResize
                                    className="w-full"
                                />
                            )}
                        />
                    </div>

                    {showIcon && (
                        <div className="col-12 md:col-3">
                            <label className="block mb-2">Icon</label>

                            <Controller
                                name={`benefitsPerksPackage.${index}.icon`}
                                control={control}
                                render={({ field }) => (
                                    <Dropdown
                                        value={field.value}
                                        options={benefitIconOptions}
                                        onChange={(e) =>
                                            field.onChange(e.value)
                                        }
                                        placeholder="Select icon"
                                        className="w-full"
                                    />
                                )}
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <Dialog visible={props.open} style={{ width: "80vw", maxWidth: "1400px" }} header="Float Requirement Publicly" footer={dFooter} modal maximizable className="p-fluid" onHide={onHide}>
            <form id="publishRequirementForm" onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid" autoComplete="off">
                <div className="col-12"><h5>Basic Job Information</h5></div>

                <JVInput disabled required column={3} label="Req ID" control={control} name="req_ID" error={errors.req_ID?.message} />
                <JVDropdown column={3} label="Industry Type" name="industryType" control={control} options={industryTypeOptions} optionValue="id" optionLabel="name" placeholder="Select industry type" required error={errors.industryType?.message} />
                <JVInput required column={3} label="Public Display Job Title" control={control} name="req_Job_Title" error={errors.req_Job_Title?.message} />
                <JVInput disabled required column={3} label="Duration" control={control} name="req_Duration" error={errors.req_Duration?.message} />
                <JVDropdown column={3} label="Job Term" name="req_Term" control={control} options={termOptions} optionValue="id" optionLabel="name" placeholder="Select job term" required error={errors.req_Term?.message} />

                <JVDropdown column={3} label="Job Type" name="job_Type" control={control} options={jobTypeOptions} optionValue="id" optionLabel="name" placeholder="Select job type" required error={errors.job_Type?.message} />

                <JVInput required column={3} label="Location" control={control} name="location" error={errors.location?.message} />

                <JVInput required column={4} label="Number of Openings" control={control} name="no_Of_Openings" error={errors.no_Of_Openings?.message} />

                <div className="col-12 md:col-3">
                    <label className="block mb-2">Posted Date</label>
                    <Controller name="req_Posted_Date" control={control}
                        render={({ field }) => (
                            <input type="date" className="p-inputtext p-component w-full"
                                value={field.value instanceof Date && !Number.isNaN(field.value.getTime()) ? field.value.toISOString().slice(0, 10) : ""}
                                onChange={(e) =>
                                    field.onChange(
                                        e.target.value
                                            ? new Date(`${e.target.value}T00:00:00`)
                                            : null
                                    )
                                }
                            />
                        )}
                    />
                    {errors.req_Posted_Date?.message && (
                        <small className="p-error">
                            {errors.req_Posted_Date.message}
                        </small>
                    )}
                </div>

                <div className="col-12 md:col-3">
                    <label className="block mb-2">Application Deadline</label>
                    <Controller name="application_Deadline" control={control}
                        render={({ field }) => (
                            <input type="datetime-local" className="p-inputtext p-component w-full" value={field.value ?? ""} onChange={field.onChange} />
                        )}
                    />
                </div>


                <JVCheckbox column={4} label="Req Hold Status" control={control} name="active" error={errors.active?.message} className="flex align-items-center gap-2 white-space-nowrap pt-4" />



                <Divider className="col-12" />

                <div className="col-12">
                    <h5>Job Description</h5>
                </div>

                <div className="col-3">
                    <label className="block mb-2" htmlFor="description">
                        Job Description
                    </label>

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <InputTextarea id="description" name={field.name} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value)} onBlur={field.onBlur} rows={2} autoResize className="w-full" placeholder="Enter job description" />
                        )}
                    />

                    {errors.description?.message && (
                        <small className="p-error">
                            {errors.description.message}
                        </small>
                    )}
                </div>

                <JVInput column={3} label="About Role Title" control={control} name="aboutRoleTitle" />
                <JVInput column={3} label=" About Role Mission" control={control} name="aboutRoleMission" />



                <Divider className="col-12" />
                <div className="col-12">
                    <div className="grid align-items-center justify-content-between mb-3">
                        <div className="col-auto">
                            <h3 className="text-lg font-semibold m-0">
                                Key Responsibilities
                            </h3>
                        </div>

                        <div className="col-auto">
                            <Button type="button" label="Add More" icon="pi pi-plus" onClick={() => appendResponsibility({ ...emptySection })} />
                        </div>
                    </div>


                    {responsibilityFields.map((item, index) => (
                        <div
                            key={item.id}
                            className="grid border-1 surface-border border-round p-3 mb-3"
                        >
                            <div className="col-12 flex align-items-center">
                                <strong>Key Responsibilities {index + 1}</strong>

                                <div className="ml-auto">
                                    <Button
                                        type="button"
                                        label="Remove" style={{ backgroundColor: "#dc2626", borderColor: "#dc2626", color: "#ffffff", }}
                                        icon="pi pi-trash"
                                        disabled={responsibilityFields.length === 1}
                                        onClick={() => removeResponsibility(index)}
                                    />
                                </div>
                            </div>

                            <JVInput column={3} label="Title" control={control} name={`keyResponsibilities.${index}.title` as any} />

                            <div className="col-12 md:col-6">
                                <label className="block mb-2">Description</label>
                                <Controller
                                    name={`keyResponsibilities.${index}.description` as any}
                                    control={control}
                                    render={({ field }) => (
                                        <InputTextarea {...field} value={field.value ?? ""} rows={2} autoResize className="w-full" />
                                    )}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <Divider className="col-12" />

                {/* 4. Required Skills & Experience */}
                <div className="col-12">
                    <div className="grid align-items-center justify-content-between mb-3">
                        <div className="col-auto">
                            <h3 className="text-lg font-semibold m-0"> Required Skills & Experience</h3>
                        </div>

                        <div className="col-auto">
                            <Button type="button" label="Add More" icon="pi pi-plus" onClick={() => appendSkill({ ...emptySection })} />
                        </div>
                    </div>

                    {skillFields.map((item, index) => (
                        <div key={item.id} className="grid border-1 surface-border border-round p-3 mb-3">
                            <div className="col-12 flex align-items-center">
                                <strong>Required Skills & Experience {index + 1}</strong>

                                <div className="ml-auto">
                                    <Button type="button" label="Remove" icon="pi pi-trash" style={{ backgroundColor: "#dc2626", borderColor: "#dc2626", color: "#ffffff", }} disabled={skillFields.length === 1} onClick={() => removeSkill(index)} />
                                </div>
                            </div>

                            <JVInput column={3} label="Title" control={control} name={`requiredSkillsExperience.${index}.title` as any} />

                            <div className="col-12 md:col-6">
                                <label className="block mb-2">Description</label>

                                <Controller name={`requiredSkillsExperience.${index}.description` as any} control={control}
                                    render={({ field }) => (<InputTextarea {...field} value={field.value ?? ""} rows={2} autoResize className="w-full" />)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <Divider className="col-12" />

                {/* 5. Preferred Qualifications */}
                <div className="col-12">
                    <div className="grid align-items-center justify-content-between mb-3">
                        <div className="col-auto">
                            <h3 className="text-lg font-semibold m-0">Preferred Qualifications</h3>
                        </div>

                        <div className="col-auto">
                            <Button type="button" label="Add More" icon="pi pi-plus" onClick={() => appendQualification({ ...emptySection })} />
                        </div>
                    </div>

                    {qualificationFields.map((item, index) => (
                        <div key={item.id} className="grid border-1 surface-border border-round p-3 mb-3">
                            <div className="col-12 flex align-items-center">
                                <strong>Preferred Qualifications {index + 1}</strong>

                                <div className="ml-auto">
                                    <Button type="button" label="Remove" icon="pi pi-trash" style={{ backgroundColor: "#dc2626", borderColor: "#dc2626", color: "#ffffff", }} disabled={qualificationFields.length === 1} onClick={() => removeQualification(index)} />
                                </div>
                            </div>

                            <JVInput column={3} label="Title" control={control} name={`preferredQualifications.${index}.title` as any} />

                            <div className="col-12 md:col-6">
                                <label className="block mb-2">Description</label>

                                <Controller
                                    name={`preferredQualifications.${index}.description` as any}
                                    control={control}
                                    render={({ field }) => (<InputTextarea {...field} value={field.value ?? ""} rows={2} autoResize className="w-full"
                                    />
                                    )}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <Divider className="col-12" />

                {/* 6. Benefits & Perks */}
                <div className="col-12">
                    <div className="grid align-items-center justify-content-between mb-3">
                        <div className="col-auto">
                            <h3 className="text-lg font-semibold m-0">Benefits & Perks</h3>
                        </div>

                        <div className="col-auto">
                            <Button type="button" label="Add More" icon="pi pi-plus" onClick={() => appendBenefit({ ...emptySection, icon: "HeartPulse", })} />
                        </div>
                    </div>

                    {benefitFields.map((item, index) => (
                        <div key={item.id} className="grid border-1 surface-border border-round p-3 mb-3">
                            <div className="col-12 flex align-items-center">
                                <strong>Benefits & Perks {index + 1}</strong>

                                <div className="ml-auto">
                                    <Button type="button" label="Remove" icon="pi pi-trash" style={{ backgroundColor: "#dc2626", borderColor: "#dc2626", color: "#ffffff", }} disabled={benefitFields.length === 1} onClick={() => removeBenefit(index)} />
                                </div>
                            </div>

                            <JVInput column={4} label="Title" control={control} name={`benefitsPerksPackage.${index}.title` as any} />

                            <div className="col-12 md:col-5">
                                <label className="block mb-2">Description</label>

                                <Controller name={`benefitsPerksPackage.${index}.description` as any} control={control}
                                    render={({ field }) => (<InputTextarea {...field} value={field.value ?? ""} rows={2} autoResize className="w-full" />)}
                                />
                            </div>

                            <JVDropdown label="Icon" column={3} name={`benefitsPerksPackage.${index}.icon`} control={control} options={benefitIconOptions} optionValue="id" optionLabel="name" placeholder="Select icon" />

                        </div>
                    ))}
                </div>

                <Divider className="col-12" />
                <div className="col-12"><h5>Location & Work Model</h5></div>
                <JVInput column={3} label="Primary Location" control={control} name="primaryLocation" />

                <JVInput column={3} label="Work Model" control={control} name="workModel" />

                <div className="col-12">
                    <label className="block mb-2" htmlFor="workModelDescription">
                        Work Model Description
                    </label>

                    <Controller
                        name="workModelDescription"
                        control={control}
                        render={({ field }) => (<InputTextarea id="workModelDescription" name={field.name} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value)} onBlur={field.onBlur} rows={3} autoResize className="w-full" placeholder="Describe the work model" />)}
                    />

                    {errors.workModelDescription?.message && (
                        <small className="p-error">
                            {errors.workModelDescription.message}
                        </small>
                    )}
                </div>

                <Divider className="col-12" />

                <div className="col-12"><h5>Compensation</h5></div>

                <JVInput column={3} label="Minimum Salary" control={control} name="salaryMin" />

                <JVInput column={3} label="Maximum Salary" control={control} name="salaryMax" />

                <JVInput column={3} label="Currency" control={control} name="salaryCurrency" />
                <JVDropdown column={3} label="Salary Period" name="salaryPeriod" control={control} options={salaryPeriodOptions} optionValue="id" optionLabel="name" placeholder="Select salary period" error={errors.salaryPeriod?.message} />

                <JVInput column={3} label="Equity" control={control} name="equityDescription" />

                <Divider className="col-12" />

                {/* 9. Company / Employer */}
                <div className="col-12"><h5>Company / Employer</h5></div>

                <JVInput column={3} label="Company Name" control={control} name="companyName" />

                <JVInput column={3} label="Company Category" control={control} name="companyCategory" />

                <div className="col-3 mt-5">
                    <Controller
                        name="isVerifiedEmployer"
                        control={control}
                        render={({ field }) => (
                            <div className="flex align-items-center gap-2">
                                <Checkbox
                                    inputId="isVerifiedEmployer"
                                    checked={field.value}
                                    onChange={(e) =>
                                        field.onChange(e.checked)
                                    }
                                />
                                <label htmlFor="isVerifiedEmployer">
                                    Verified Employer
                                </label>
                            </div>
                        )}
                    />
                </div>

                <Divider className="col-12" />

                {/* 10. Referral Details */}
                <div className="col-12"><h5>Referral Details</h5></div>

                <JVInput column={3} label="Referral Reward" control={control} name="referralReward" />

                <JVInput column={3} label="Reward Currency" control={control} name="referralRewardCurrency" />

                <JVInput column={3} label="Milestone Days" control={control} name="referralMilestoneDays" />

                <div className="col-12">
                    <label className="block mb-2" htmlFor="referralDescription">Referral Description</label>

                    <Controller
                        name="referralDescription"
                        control={control}
                        render={({ field }) => (
                            <InputTextarea
                                id="referralDescription"
                                name={field.name}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value)}
                                onBlur={field.onBlur}
                                rows={4}
                                autoResize
                                className="w-full"
                                placeholder="Enter referral description"
                            />
                        )}
                    />
                    {errors.referralDescription?.message && (
                        <small className="p-error">
                            {errors.referralDescription.message}
                        </small>
                    )}
                    <Divider className="col-12" />



                    <div className="col-12">
                        <h5>Hiring Partner</h5>

                        <div className="grid">
                            <JVInput column={4} label="Name" control={control} name="hiringPartnerName" />
                            <JVInput column={4} label="Designation" control={control} name="hiringPartnerDesignation" />
                            <JVInput column={4} label="Profile Image" control={control} name="hiringPartnerImage" />

                            <div className="col-12">
                                <label className="block mb-2" htmlFor="hiringPartnerMessage">
                                    Hiring Partner Message
                                </label>

                                <Controller
                                    name="hiringPartnerMessage"
                                    control={control}
                                    render={({ field }) => (
                                        <InputTextarea
                                            {...field}
                                            id="hiringPartnerMessage"
                                            value={field.value ?? ""}
                                            rows={4}
                                            autoResize
                                            className="w-full"
                                            placeholder="Enter hiring partner message"
                                        />
                                    )}
                                />

                                {errors.hiringPartnerMessage?.message && (
                                    <small className="p-error">
                                        {errors.hiringPartnerMessage.message}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Dialog>
    );
};

export default PublishReqPublic;
